import type { FormErrors, MatrimonyFormData } from "src/types/matrimonyForm.types";

const isBlank = (value: string | undefined | null) => !value || value.trim().length === 0;

const MAX_PHOTO_BYTES = 1 * 1024 * 1024; // 1MB

const DOSHAM_VALUES = new Set([
	"NONE",
	"CHEVVAY_DOSHAM",
	"NAGA_DOSHAM",
	"KALA_SARPA_DOSHAM",
	"RAHU_KETU_DOSHAM",
	"OTHERS",
]);

export const validateStep = (step: number, formData: MatrimonyFormData): FormErrors => {
	const nextErrors: FormErrors = {};

	const requireField = (field: keyof MatrimonyFormData, message: string) => {
		const raw = formData[field];
		// `MatrimonyFormData` has non-string fields (e.g., `photoFile?: File | null`).
		// Only string fields should be checked via `.trim()`.
		if (raw == null) {
			nextErrors[field] = message;
			return;
		}
		if (typeof raw === "string") {
			if (raw.trim().length === 0) nextErrors[field] = message;
			return;
		}
		// For non-string values, treat existence as "provided".
	};

	if (step === 1) {
		// Photo validations
		if (!formData.photoFile) {
			nextErrors.photoFile = "Profile photo is required";
		} else if (formData.photoFile.size > MAX_PHOTO_BYTES) {
			nextErrors.photoFile = "Photo must be 1MB or less";
		}

		requireField("fullName", "Full name is required");
		requireField("dob", "Date of birth is required");

		// Prevent future dates
		if (formData.dob && formData.dob.trim()) {
			const dobDate = new Date(formData.dob.trim());
			if (!Number.isNaN(dobDate.getTime()) && dobDate > new Date()) {
				nextErrors.dob = "Date of birth cannot be a future date";
			}
		}

		requireField("emailId", "Email is required");
		requireField("mobileNumber", "Mobile number is required");
		requireField("maritalStatus", "Marital status is required");
	}

	if (step === 2) {
		requireField("highestEducation", "Highest education is required");
		requireField("employmentSector", "Employment sector is required");
		requireField("designation", "Designation is required");
	}

	if (step === 3) {
		requireField("fatherName", "Father's name is required");
		requireField("motherName", "Mother's name is required");
		requireField("fatherOccupation", "Father's occupation is required");
		requireField("motherOccupation", "Mother's occupation is required");
		requireField("familyStatus", "Family status is required");
	}

	if (step === 4) {
		requireField("heightCm", "Height is required");
		requireField("dietaryHabits", "Dietary habits are required");
	}

	if (step === 5) {
		// If user doesn't know horoscope details, skip validations for step 5.
		if (formData.horoUnknown === "true") return nextErrors;

		// Dosham is required when horoscope details are known.
		if (isBlank(formData.dosham)) {
			nextErrors.dosham = "Please select Dosham";
		} else if (!DOSHAM_VALUES.has(formData.dosham)) {
			nextErrors.dosham = "Invalid Dosham";
		}
	}

	return nextErrors;
};
